import React, { useMemo } from 'react';
import { CurrencyAmount } from '../../CurrencyAmount';
import { formatNumber } from '../utils/formatters';
import { UsageDetailsData, DailyUsageData } from '../../../utils/openai-api';
import { DateRange } from '../../../constants/ui';

interface FilteredDataWithExtras extends UsageDetailsData {
  totalCost: number;
  totalRequests: number;
  totalContextTokens: number;
  totalGeneratedTokens: number;
  totalCachedTokens: number;
  totalApiKeys: number;
  displayedApiKeys: number;
  dateRangeInfo?: {
    startDateIso: string;
    endDateIso: string;
    startDateLabel: string;
    endDateLabel: string;
    label: string;
    timezoneOffsetMinutes: number;
  };
  dailyUsage?: DailyUsageData[];
}

/** 1日の予想コストの倍率（JST 9:00-10:00 = UTC 0:00-1:00 の実績に基づく） */
const DAILY_PROJECTION_MULTIPLIER = 15;

interface SummarySectionProps {
  filteredData: FilteredDataWithExtras | null;
  dateRange?: DateRange;
}

export const SummarySection: React.FC<SummarySectionProps> = React.memo(({ filteredData, dateRange }) => {
  // Today表示時の1日予想コスト
  // JST 9:00-11:00 (UTC 0:00-2:00) のバケットが揃えば平均×15、1つだけなら×15
  const projectedDailyCost = useMemo(() => {
    if (dateRange !== '1day' || !filteredData?.dailyUsage?.length) return null;

    // UTC 0:00-2:00 (JST 9:00-12:00) のバケットから利用可能なものの平均 × 15
    const hours = [' 00:00:00', ' 01:00:00', ' 02:00:00']
      .map(suffix => filteredData.dailyUsage.find(d => d.date.endsWith(suffix)))
      .filter((b): b is DailyUsageData => b != null && b.cost > 0);

    if (hours.length === 0) return null;

    const avgCost = hours.reduce((sum, b) => sum + b.cost, 0) / hours.length;
    return avgCost * DAILY_PROJECTION_MULTIPLIER;
  }, [dateRange, filteredData?.dailyUsage]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-2.5 rounded-md border border-blue-200 dark:border-blue-700">
        <h3 className="text-[11px] font-medium text-blue-700 dark:text-blue-300">Total Requests</h3>
        <p className="text-base font-bold text-blue-900 dark:text-blue-100 mt-0.5">{formatNumber(filteredData?.totalRequests || 0)}</p>
      </div>
      <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-2.5 rounded-md border border-green-200 dark:border-green-700">
        <h3 className="text-[11px] font-medium text-green-700 dark:text-green-300">Total Cost</h3>
        <div className="mt-0.5">
          <CurrencyAmount
            amount={filteredData?.totalCost || 0}
            className="text-base font-bold text-green-900 dark:text-green-100"
            showToggleHint={true}
          />
          {projectedDailyCost !== null && (
            <div className="mt-0.5 flex items-baseline gap-1">
              <span className="text-[9px] text-yellow-600 dark:text-yellow-400">est.</span>
              <CurrencyAmount
                amount={projectedDailyCost}
                className="text-[11px] font-medium text-yellow-600 dark:text-yellow-400"
                showToggleHint={false}
              />
              <span className="text-[8px] text-yellow-500 dark:text-yellow-500">(9-10h x{DAILY_PROJECTION_MULTIPLIER})</span>
            </div>
          )}
        </div>
      </div>
      <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-2.5 rounded-md border border-purple-200 dark:border-purple-700">
        <h3 className="text-[11px] font-medium text-purple-700 dark:text-purple-300">Input Tokens</h3>
        <p className="text-base font-bold text-purple-900 dark:text-purple-100 mt-0.5">{formatNumber(filteredData?.totalContextTokens || 0)}</p>
      </div>
      <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 p-2.5 rounded-md border border-orange-200 dark:border-orange-700">
        <h3 className="text-[11px] font-medium text-orange-700 dark:text-orange-300">Output Tokens</h3>
        <p className="text-base font-bold text-orange-900 dark:text-orange-100 mt-0.5">{formatNumber(filteredData?.totalGeneratedTokens || 0)}</p>
      </div>
      <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/20 dark:to-cyan-800/20 p-2.5 rounded-md border border-cyan-200 dark:border-cyan-700">
        <h3 className="text-[11px] font-medium text-cyan-700 dark:text-cyan-300">API Keys</h3>
        <p className="text-base font-bold text-cyan-900 dark:text-cyan-100 mt-0.5">
          <span className="text-emerald-400">{filteredData?.apiKeyUsage?.filter(k => k.numberOfRequests > 0).length ?? 0}</span>
          <span className="text-gray-500 text-xs font-normal"> / </span>
          <span>{filteredData?.totalApiKeys || 0}</span>
        </p>
      </div>
      <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 p-2.5 rounded-md border border-indigo-200 dark:border-indigo-700">
        <h3 className="text-[11px] font-medium text-indigo-700 dark:text-indigo-300">Avg Cost/Day</h3>
        <div className="mt-0.5">
          <CurrencyAmount
            amount={filteredData && filteredData.totalApiKeys > 0 && filteredData.dateRangeInfo
              ? (() => {
                  const start = new Date(filteredData.dateRangeInfo.startDateIso).getTime();
                  const end = new Date(filteredData.dateRangeInfo.endDateIso).getTime();
                  const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
                  return filteredData.totalCost / filteredData.totalApiKeys / days;
                })()
              : 0
            }
            className="text-base font-bold text-indigo-900 dark:text-indigo-100"
            showToggleHint={false}
          />
        </div>
      </div>
    </div>
  );
});
SummarySection.displayName = 'SummarySection';
